// El pie de una foto del lienzo — ADR-2026-09-16-la-card-de-info-del-lienzo.
//
// Una fila del ancho de la foto en pantalla, en la capa que React Flow NO escala con el
// zoom (`NodeToolbar`, como la barra de acciones): el rótulo
// (ADR-2026-09-16-el-titulo-de-la-foto-es-el-producto) con un chevron a su derecha, de
// la misma letra, que es lo que abre la info. Asoma al pasar el mouse o con la foto
// seleccionada, y se queda mientras la info está abierta.
//
// La info despliega, con un brote "gooey", las cards de la imagen: UNA con los datos
// (resolución, peso y cuándo se generó, en la forma en que uno lo diría) y una por cada
// producto, modelo y fondo con los que se generó, que al tocarlas se agregan al chat.
//
// El brote es una animación por card con retardo escalonado, y mientras dura el
// contenedor lleva el filtro `#lienzo-goo` (blur + umbral de alpha), que hace que las
// cards salgan del chevron como una gota que se separa; al terminar el filtro se saca,
// así el texto queda nítido y el filtro no cuesta nada en reposo.

import { useContext, useEffect, useRef, useState } from 'react';
import { NodeToolbar, Position, useViewport } from '@xyflow/react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { formatearPeso } from '../adjuntosChatAtlas';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import {
  adjuntoDeFondoDeBiblioteca,
  adjuntoDeFondoGuardado,
  adjuntoDeModelo,
  adjuntoDeProducto,
} from '../ui/bibliotecaDeAdjuntos';
import type { CeldaParaAccion } from './accionesDeCelda';
import { ANCHO_CELDA } from './acomodo';
import { fechaDeInfo } from './fechaDeInfo';
import { LienzoContext } from './lienzoContext';
import { useInfoDeImagen } from './useInfoDeImagen';
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

/** Aire entre la foto y el pie. */
const SEPARACION_PIE = 12;
/** Lo que dura el brote con el filtro puesto (la card más tardía + su animación). */
const DURACION_BROTE_MS = 700;

interface CardDeInfo {
  clave: string;
  titulo: string;
  detalle: string;
  imageUrl?: string;
  /** Con esto la card se toca y se agrega al chat; sin esto sólo informa. */
  adjunto?: ChatAttachment;
}

/** La card de datos: varias líneas rótulo → valor en un mismo container. */
interface Dato {
  rotulo: string;
  valor: string;
}

/** La caja de toda card: borde, fondo y sombra. La disposición la pone cada una. */
const CLASES_CAJA =
  'rounded-2xl border border-[rgb(var(--brand-black-rgb)_/_0.06)] bg-white text-left shadow-[0_6px_20px_rgb(var(--brand-black-rgb)_/_0.10)]';
const CLASES_CARD = `flex items-center gap-2.5 py-2 pl-2 pr-3 ${CLASES_CAJA}`;

const Card: React.FC<{ card: CardDeInfo; indice: number; onAgregar?: (a: ChatAttachment) => void }> = ({
  card,
  indice,
  onAgregar,
}) => {
  const [agregada, setAgregada] = useState(false);
  const contenido = (
    <>
      {card.imageUrl ? (
        <img src={card.imageUrl} alt="" className="h-9 w-9 shrink-0 rounded-lg object-cover" />
      ) : null}
      <span className="min-w-0">
        <span className="block truncate text-[12.5px] font-semibold text-brand-black">{card.titulo}</span>
        <span className="block truncate text-[11px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">{card.detalle}</span>
      </span>
      {card.adjunto && (
        <span
          className={`ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors ${
            agregada ? 'bg-brand-primary text-white' : 'bg-[#F0F2F7] text-[rgb(var(--brand-black-rgb)_/_0.45)]'
          }`}
          aria-hidden="true"
        >
          {agregada ? <Check className="h-3.5 w-3.5" /> : <span className="text-[14px] leading-none">+</span>}
        </span>
      )}
    </>
  );
  const estilo = { animationDelay: `${indice * 55}ms` };
  if (!card.adjunto || !onAgregar) {
    return (
      <div className={`lienzo-brotar ${CLASES_CARD}`} style={estilo}>
        {contenido}
      </div>
    );
  }
  const adjunto = card.adjunto;
  return (
    <button
      type="button"
      className={`lienzo-brotar nodrag nopan ${CLASES_CARD} transition-colors hover:bg-[#F7F8FB]`}
      style={estilo}
      title={`Agregar ${card.titulo} al chat`}
      onClick={() => {
        onAgregar(adjunto);
        setAgregada(true);
        window.setTimeout(() => setAgregada(false), 1200);
      }}
    >
      {contenido}
    </button>
  );
};

/** Las líneas de la card de datos: sólo las que se saben (o se están buscando). */
function datosDe(resuelta: ReturnType<typeof useInfoDeImagen>, createdAt: string | null): Dato[] {
  if (!resuelta) return [];
  const { info, peso } = resuelta;
  const out: Dato[] = [];
  const fecha = fechaDeInfo(createdAt);
  if (fecha) out.push({ rotulo: 'Fecha', valor: fecha });
  if (info.resolucion) {
    out.push({ rotulo: 'Resolución', valor: `${info.resolucion.ancho} × ${info.resolucion.alto} px` });
  }
  if (peso !== null) out.push({ rotulo: 'Peso', valor: peso === undefined ? '…' : formatearPeso(peso) });
  return out;
}

function cardsDe(resuelta: ReturnType<typeof useInfoDeImagen>): CardDeInfo[] {
  if (!resuelta) return [];
  const { info } = resuelta;
  const out: CardDeInfo[] = [];
  for (const p of info.productos) {
    out.push({
      clave: `prod-${p.id}`,
      titulo: p.name,
      detalle: 'Producto',
      imageUrl: p.imageUrl,
      adjunto: adjuntoDeProducto(p),
    });
  }
  for (const m of info.modelos) {
    out.push({
      clave: `model-${m.id}`,
      titulo: m.nombre,
      detalle: palabraDeModeloActual().titulo,
      imageUrl: m.thumbnailUrl,
      adjunto: adjuntoDeModelo(m),
    });
  }
  for (const f of info.fondos) {
    out.push({
      clave: `fondo-${f.tipo}-${f.fondo.id}`,
      titulo: f.fondo.nombre,
      detalle: 'Fondo',
      imageUrl: f.fondo.imageUrl,
      adjunto: f.tipo === 'guardado' ? adjuntoDeFondoGuardado(f.fondo) : adjuntoDeFondoDeBiblioteca(f.fondo),
    });
  }
  return out;
}

export const PieDeCelda: React.FC<{
  titulo: string;
  /** Lo que mide la celda en el lienzo: el rótulo la calca. Cada celda tiene su ancho
   *  desde ADR-2026-09-22-las-celdas-del-lienzo-ocupan-la-misma-superficie. */
  ancho?: number;
  visible: boolean;
  /** La celda accionable (con fila y miniatura); sin ella no hay botón de Info. */
  celda: CeldaParaAccion | null;
  abierta: boolean;
  onAbrir: (abierta: boolean) => void;
  /** El puntero entró o salió del pie: la foto lo cuenta como hover propio, así el pie
   *  no se va justo cuando uno baja a tocar el chevron. */
  onHover?: (dentro: boolean) => void;
}> = ({ titulo, ancho, visible, celda, abierta, onAbrir, onHover }) => {
  const lienzo = useContext(LienzoContext);
  const { zoom } = useViewport();
  const resuelta = useInfoDeImagen(celda, abierta);
  const datos = abierta ? datosDe(resuelta, celda?.datos.createdAt ?? null) : [];
  const cards = abierta ? cardsDe(resuelta) : [];
  // El filtro gooey sólo mientras brota.
  const [brotando, setBrotando] = useState(false);
  useEffect(() => {
    if (!abierta) return;
    setBrotando(true);
    const t = window.setTimeout(() => setBrotando(false), DURACION_BROTE_MS);
    return () => window.clearTimeout(t);
  }, [abierta]);
  // Un toque fuera lo cierra (el pie está en un portal: el click no llega al nodo).
  const raizRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!abierta) return;
    const alTocar = (e: PointerEvent) => {
      if (raizRef.current && !raizRef.current.contains(e.target as Node)) onAbrir(false);
    };
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onAbrir(false);
    };
    document.addEventListener('pointerdown', alTocar, true);
    document.addEventListener('keydown', alTeclado);
    return () => {
      document.removeEventListener('pointerdown', alTocar, true);
      document.removeEventListener('keydown', alTeclado);
    };
  }, [abierta, onAbrir]);

  return (
    <NodeToolbar isVisible={visible || abierta} position={Position.Bottom} align="start" offset={SEPARACION_PIE}>
      <div
        ref={raizRef}
        className="flex flex-col items-stretch"
        style={{ width: Math.max((ancho ?? ANCHO_CELDA) * zoom, 160) }}
        onMouseEnter={() => onHover?.(true)}
        onMouseLeave={() => onHover?.(false)}
      >
        {/* El rótulo y su chevron son UN botón cuando hay info que abrir: misma letra,
            mismo color, sin pastilla — "con la misma onda que el título". */}
        {celda ? (
          <button
            type="button"
            onClick={() => onAbrir(!abierta)}
            aria-expanded={abierta}
            aria-label={abierta ? 'Cerrar la info de la imagen' : 'Ver la info de la imagen'}
            className="nodrag nopan flex min-w-0 items-center gap-1 self-start text-left text-[12.5px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.75)] transition-colors hover:text-brand-black"
          >
            <span className="min-w-0 truncate">{titulo}</span>
            <ChevronDown
              className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${abierta ? 'rotate-180' : ''}`}
              strokeWidth={2.25}
              aria-hidden="true"
            />
          </button>
        ) : (
          <span className="pointer-events-none min-w-0 truncate text-[12.5px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.75)]">
            {titulo}
          </span>
        )}
        {abierta && (
          <div className={`mt-2 flex flex-col items-start gap-1.5 ${brotando ? 'lienzo-goo' : ''}`}>
            {/* 240px y valores sin quiebre: con 180 la resolución ("848 × 1264 px") se partía
                en dos renglones (pedido de Matías del 2026-09-17). */}
            {datos.length > 0 && (
              <dl className={`lienzo-brotar flex min-w-[240px] flex-col gap-1 px-3 py-2.5 ${CLASES_CAJA}`}>
                {datos.map((d) => (
                  <div key={d.rotulo} className="flex items-baseline justify-between gap-4">
                    <dt className="whitespace-nowrap text-[11px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">{d.rotulo}</dt>
                    <dd className="whitespace-nowrap text-[12.5px] font-semibold text-brand-black">{d.valor}</dd>
                  </div>
                ))}
              </dl>
            )}
            {datos.length === 0 && cards.length === 0 && (
              <div className={`lienzo-brotar ${CLASES_CARD}`}>
                <Loader2 className="h-4 w-4 animate-spin text-[rgb(var(--brand-black-rgb)_/_0.45)]" aria-hidden="true" />
                <span className="text-[12.5px] text-[rgb(var(--brand-black-rgb)_/_0.6)]">Buscando…</span>
              </div>
            )}
            {cards.map((c, i) => (
              <Card key={c.clave} card={c} indice={i} onAgregar={lienzo.agregarAdjunto} />
            ))}
          </div>
        )}
      </div>
    </NodeToolbar>
  );
};
